import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['power', 'circular', 'pill', 'material', 'neon'] as const;

const THEME_ALIASES: Record<string, string> = {
  'round': 'circular',
  'flat': 'material',
  'glow': 'neon',
  'bright': 'neon',
};

export class PushButtonCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Theme patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Color patterns
    for (const [regex, handler] of createColorPatterns(['pressColor', 'releaseColor', 'glowColor', 'borderColor', 'textColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // Shape
    this.commandPatterns.set(
      /(?:set )?shape (?:to )?(rounded[- ]?rect|circle|circular|round|pill)/i,
      (match) => {
        const raw = match[1].toLowerCase().replace(/\s+/g, '-');
        const shapeMap: Record<string, string> = {
          circle: 'circular', round: 'circular', circular: 'circular',
          pill: 'pill',
          'rounded-rect': 'rounded-rect', roundedrect: 'rounded-rect',
        };
        return { properties: { shape: shapeMap[raw] || 'circular' } };
      }
    );

    // Size
    this.commandPatterns.set(
      /(?:set )?size (?:to )?(sm|small|md|medium|lg|large)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          sm: 'sm', small: 'sm', md: 'md', medium: 'md', lg: 'lg', large: 'lg',
        };
        return { properties: { size: sizeMap[match[1].toLowerCase()] || 'md' } };
      }
    );

    // Label
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim() } })
    );

    // Icon
    this.commandPatterns.set(
      /(?:set )?icon (?:to )?(\w+)/i,
      (match) => ({ properties: { icon: match[1].trim() } })
    );

    // Glow intensity
    this.commandPatterns.set(
      /(?:set )?glow intensity (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { glowIntensity: parseFloat(match[1]) } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.PUSH_BUTTON && this.hasKeywords(command);
  }

  private hasKeywords(command: string): boolean {
    const keywords = [
      "push button", "button", "press", "shape", "glow", "theme", "size",
      "label", "icon", "circular", "pill", "color",
      "power", "material", "neon",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
      if (patternMatch?.style) {
        changes.style = { ...changes.style, ...patternMatch.style };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand push button command",
        suggestions: await this.getExampleCommands(ComponentType.PUSH_BUTTON),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    if (changes.properties?.glowIntensity != null) {
      const intensity = changes.properties.glowIntensity as number;
      if (intensity < 0 || intensity > 30) {
        return { valid: false, error: "Glow intensity must be between 0 and 30" };
      }
    }
    return super.validateChanges(changes, component);
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set shape to circular",
      "Theme neon",
      "Glow orange",
      "Press color red",
      "Size large",
      "Set label to Power",
      "Set icon to Power",
      "Set glow intensity to 12",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const currentTheme = (props.theme as string) ?? 'power';

    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    suggestions.push("Set shape to circular");
    suggestions.push("Glow orange");
    suggestions.push("Press color red");
    suggestions.push("Size large");
    suggestions.push("Set label to Power");

    return suggestions.slice(0, 6);
  }
}

export const pushButtonCommandHandler = new PushButtonCommandHandler();
